import React, { memo, useCallback, useContext } from 'react';
import { View } from 'react-native';
import { rem, Spacer } from 'design-system';
import { IconArrow } from 'design-system/icons/IconArrow';
import { IconRocket } from 'design-system/icons/IconRocket';
import Squircle from 'design-system/Squircle';
import Status from 'design-system/Status';
import Text from 'design-system/Text';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
import { useAppSelector } from 'store/hooks';
import { InvestWalkthroughContext } from 'features/feed/components/walkthrough/context';
import { getNextCollectionLabel, getPausedUntilLabel } from '../utils/autoinvest';
import { blockInvestActionOnWeb } from '../webGate';
import { selectActiveTradingAccountId, selectAutoInvestPlanForAccount, selectAutoInvestStatusObj, } from '../reducers/selectors';
const AutoInvestCard = ({ accountType }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const navigation = useMainStackNavigation();
    const tradingAccountId = useAppSelector(selectActiveTradingAccountId);
    const isInInvestWalkthrough = useContext(InvestWalkthroughContext);
    const plan = useAppSelector((state) => tradingAccountId ? selectAutoInvestPlanForAccount(state, tradingAccountId) : undefined);
    const statusObj = useAppSelector((state) => selectAutoInvestStatusObj(state, plan?.accountId));
    const handlePress = useCallback(() => {
        if (!statusObj) {
            if (blockInvestActionOnWeb('Set up Autoinvest'))
                return;
            navigation.navigate('AutoinvestIntro', { accountType });
        }
        else {
            navigation.navigate('AutoinvestManage', { accountType });
        }
    }, [accountType, statusObj]);
    const isActive = statusObj?.isActive ?? false;
    const isPaused = statusObj?.isPaused ?? false;
    const isActionRequired = statusObj?.isActionRequired ?? false;
    // eslint-disable-next-line no-nested-ternary
    const inActiveColor = isActionRequired ? undefined : isPaused ? colors.status.offline : colors.status.informational;
    // it will be undefined until we fetch data, then we'll have data or it will be null and we show onboarding card
    if (plan === undefined)
        return null;
    return (<TouchableBounceCard onDark onPress={handlePress} noMarginBottom style={isInInvestWalkthrough ? styles.walkthroughCard : null}>
      <View style={styles.content}>
        <Squircle color={colors.background.dark}>
          <IconRocket color={colors.text.investment}/>
        </Squircle>
        <Spacer w={16}/>
        <View style={styles.titleContainer}>
          {!!statusObj && (<Status active={isActive} activeText="Working" inactiveText={isActionRequired ? 'Action required' : statusObj.displayStatus} inActiveColor={inActiveColor}/>)}
          <Text Text-16>Autoinvest</Text>
          {isPaused && !plan?.pausedUntil ? null : (<Text TextThin-14 Secondary>
              {/* eslint-disable-next-line no-nested-ternary */}
              {isActive && plan?.dayOfMonth
                ? getNextCollectionLabel(plan.dayOfMonth)
                : isPaused && plan?.pausedUntil
                    ? getPausedUntilLabel(plan.pausedUntil)
                    : 'Invest automatically every month'}
            </Text>)}
        </View>
        <Spacer w={8}/>
        <IconArrow />
      </View>
    </TouchableBounceCard>);
};
const styleSet = createStyleSheets(() => ({
    content: {
        padding: rem(16),
        alignItems: 'center',
        flexDirection: 'row',
    },
    titleContainer: {
        flex: 1,
    },
    walkthroughCard: {
        marginTop: rem(4),
        marginBottom: rem(4),
        marginHorizontal: 0,
    },
}));
export default memo(AutoInvestCard);
